import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { resourcesApi, apiPath, authTenantHeaders } from '../services/api';
import { Resource, DatabaseEndpoint, HttpEndpoint, SshEndpoint, KubernetesEndpoint, DesktopEndpoint } from '../types';
import { CheckCircle, Info } from 'lucide-react';
import { getCookie } from '../utils/cookies';
import { toast } from '@/lib/toast';
import { Banner, Button, Checkbox, Input, Modal, Select, Textarea } from '@/components/ds-zta';

interface ResourceFormProps {
  resource?: Resource | null;
  onClose: () => void;
  onSuccess: () => void;
  // Pre-selects the resource type when creating a new resource (e.g. 'k8s', 'desktop')
  // so type-specific pages can open the form scoped to their type. Ignored when editing.
  initialType?: string;
}

interface ResourceFormData {
  external_id: string;
  type: 'database' | 'http_proxy' | 'ssh' | 'k8s' | 'desktop';
  endpoint: DatabaseEndpoint | HttpEndpoint | SshEndpoint | KubernetesEndpoint | DesktopEndpoint;
  labels: Record<string, string>;
  metadata: Record<string, string>;
}

interface ConnectionTestResult {
  success: boolean;
  message: string;
}

const normalizeTypeForUI = (t?: string) => (t === 'http_proxy' ? 'http' : (t || 'database'));

export default function ResourceForm({ resource, onClose, onSuccess, initialType }: ResourceFormProps) {
  const [selectedType, setSelectedType] = useState<string>(normalizeTypeForUI(resource?.type || initialType));
  const [selectedProtocol, setSelectedProtocol] = useState<string>('');
  const [testingConnection, setTestingConnection] = useState<boolean>(false);
  const [connectionTestResult, setConnectionTestResult] = useState<ConnectionTestResult | null>(null);
  // Local state to render JSON nicely in textareas
  const [labelsText, setLabelsText] = useState<string>('{}');
  const [metadataText, setMetadataText] = useState<string>('{}');
  const [labelsJsonError, setLabelsJsonError] = useState<string | null>(null);
  const [metadataJsonError, setMetadataJsonError] = useState<string | null>(null);
  const isEditing = !!resource;

  const { register, handleSubmit, formState: { errors }, reset, setValue, getValues } = useForm<ResourceFormData>({
    defaultValues: {
      external_id: resource?.external_id || '',
      type: (resource?.type || initialType || 'database') as ResourceFormData['type'],
      endpoint: resource?.endpoint || {},
      labels: resource?.labels || {},
      metadata: resource?.metadata || {},
    }
  });
  const protocolField = register('endpoint.protocol', { required: 'Protocol is required' });

  // Get default port for database protocol
  const getDefaultPort = (protocol: string): number => {
    const portMap: Record<string, number> = {
      postgresql: 5432,
      mysql: 3306,
      redis: 6379,
      mongodb: 27017,
      mariadb: 3306,
      cassandra: 9042,
      elasticsearch: 9200,
      influxdb: 8086
    };
    return portMap[protocol] || 5432;
  };

  // Handle protocol change
  const handleProtocolChange = (protocol: string) => {
    setSelectedProtocol(protocol);
    if (protocol && !isEditing) {
      setValue('endpoint.port', getDefaultPort(protocol));
    }
  };

  const createMutation = useMutation(resourcesApi.create, {
    onSuccess: () => {
      toast.success('Resource created successfully');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create resource');
    },
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<Resource> }) => resourcesApi.update(id, data),
    {
      onSuccess: () => {
        toast.success('Resource updated successfully');
        onSuccess();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to update resource');
      },
    }
  );

  useEffect(() => {
    if (resource) {
      reset({
        external_id: resource.external_id,
        type: resource.type,
        endpoint: resource.endpoint,
        labels: resource.labels,
        metadata: resource.metadata,
      });
      setSelectedType(normalizeTypeForUI(resource.type));
      // Pretty print incoming objects into textareas
      setLabelsText(JSON.stringify(resource.labels || {}, null, 2));
      setMetadataText(JSON.stringify(resource.metadata || {}, null, 2));
      setLabelsJsonError(null);
      setMetadataJsonError(null);
    } else {
      // Initialize for create flow
      setLabelsText(JSON.stringify({}, null, 2));
      setMetadataText(JSON.stringify({}, null, 2));
      setLabelsJsonError(null);
      setMetadataJsonError(null);
    }
  }, [resource, reset]);

  const onSubmit = async (data: ResourceFormData) => {
    // Parse JSON textareas into objects; validate before submit
    let parsedLabels: Record<string, string> = {};
    let parsedMetadata: Record<string, string> = {};
    try {
      parsedLabels = labelsText?.trim() ? JSON.parse(labelsText) : {};
      setLabelsJsonError(null);
    } catch (e: any) {
      setLabelsJsonError('Invalid JSON');
      return;
    }
    try {
      parsedMetadata = metadataText?.trim() ? JSON.parse(metadataText) : {};
      setMetadataJsonError(null);
    } catch (e: any) {
      setMetadataJsonError('Invalid JSON');
      return;
    }

    // Ensure they are objects
    if (parsedLabels && typeof parsedLabels !== 'object') {
      setLabelsJsonError('Labels must be a JSON object');
      return;
    }
    if (parsedMetadata && typeof parsedMetadata !== 'object') {
      setMetadataJsonError('Metadata must be a JSON object');
      return;
    }

    // Sync parsed values into form payload
    data.labels = parsedLabels;
    data.metadata = parsedMetadata;

    const resourceData = {
      ...data,
      health_status: 'unknown' as const,
    };

    if (isEditing && resource) {
      await updateMutation.mutateAsync({ id: resource.id, data: resourceData });
    } else {
      await createMutation.mutateAsync(resourceData);
    }
  };

  // Test database connection
  const handleTestConnection = async () => {
    if (selectedType !== 'database') return;

    setTestingConnection(true);
    setConnectionTestResult(null);

    try {
      // Get current form values
      const formData = {
        protocol: (document.querySelector('select[name="endpoint.protocol"]') as HTMLSelectElement)?.value,
        host: (document.querySelector('input[name="endpoint.host"]') as HTMLInputElement)?.value,
        port: (document.querySelector('input[name="endpoint.port"]') as HTMLInputElement)?.value,
        database: (document.querySelector('input[name="endpoint.database"]') as HTMLInputElement)?.value,
        username: (document.querySelector('input[name="endpoint.username"]') as HTMLInputElement)?.value,
        password: (document.querySelector('input[name="endpoint.password"]') as HTMLInputElement)?.value,
        ssl_mode: (document.querySelector('select[name="endpoint.ssl_mode"]') as HTMLSelectElement)?.value || 'require',
      };

      // Validate required fields
      if (!formData.protocol || !formData.host || !formData.port || !formData.database || !formData.username || !formData.password) {
        setConnectionTestResult({
          success: false,
          message: 'Please fill in all required database connection fields before testing.'
        });
        return;
      }

      // Test connection via Admin API using axios with auth
      const token = getCookie('auth_token');
      if (!token) {
        setConnectionTestResult({
          success: false,
          message: 'Authentication required. Please log in again.'
        });
        return;
      }

      const response = await fetch(apiPath('/api/v1/database/test-connection'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authTenantHeaders(),
        },
        body: JSON.stringify({
          // The resource name: for host "agent-managed" the API tests through the
          // relay agent whose IAP_SERVER_ID equals it, and refuses without it.
          external_id: (getValues('external_id') || '').trim(),
          endpoint: {
            protocol: formData.protocol,
            host: formData.host,
            port: parseInt(formData.port),
            database: formData.database,
            username: formData.username,
            password: formData.password,
            ssl_mode: formData.ssl_mode,
          }
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setConnectionTestResult({
          success: true,
          message: `Successfully connected to ${formData.protocol} database at ${formData.host}:${formData.port}/${formData.database}`
        });
      } else {
        setConnectionTestResult({
          success: false,
          message: result.error || 'Connection failed. Please check your database credentials and network connectivity.'
        });
      }
    } catch (error) {
      setConnectionTestResult({
        success: false,
        message: `Connection test failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const renderEndpointFields = () => {
    switch (selectedType) {
      case 'database':
        return (
          <div className="space-y-4">
            <div>
              <Select
                label="Protocol"
                required
                {...protocolField}
                onChange={(e) => {
                  // Keep react-hook-form's own handler: replacing it left the
                  // chosen protocol out of the form's values.
                  protocolField.onChange(e);
                  handleProtocolChange(e.target.value);
                }}
              >
                <option value="">Select Protocol</option>
                <option value="postgresql">PostgreSQL (Port 5432)</option>
                <option value="mysql">MySQL (Port 3306)</option>
                <option value="redis">Redis (Port 6379)</option>
                <option value="mongodb">MongoDB (Port 27017)</option>
                <option value="mariadb">MariaDB (Port 3306)</option>
                <option value="cassandra">Cassandra (Port 9042)</option>
                <option value="elasticsearch">Elasticsearch (Port 9200)</option>
                <option value="influxdb">InfluxDB (Port 8086)</option>
              </Select>
              {errors.endpoint && (errors.endpoint as any).protocol && (
                <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).protocol.message}</p>
              )}
              <p className="mt-1 text-sm text-[var(--d2d-text-muted)]">Port will be automatically set based on protocol</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  label="Host"
                  required
                  {...register('endpoint.host', { required: 'Host is required' })}
                  type="text"
                  placeholder="localhost"
                />
                {errors.endpoint && (errors.endpoint as any).host && (
                  <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).host.message}</p>
                )}
              </div>
              <div>
                <Input
                  label="Port"
                  required
                  {...register('endpoint.port', {
                    required: 'Port is required',
                    valueAsNumber: true,
                    min: { value: 1, message: 'Port must be greater than 0' },
                    max: { value: 65535, message: 'Port must be less than 65536' }
                  })}
                  type="number"
                  placeholder={selectedProtocol ? getDefaultPort(selectedProtocol).toString() : "5432"}
                />
                {errors.endpoint && (errors.endpoint as any).port && (
                  <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).port.message}</p>
                )}
              </div>
            </div>
            <div>
              <Input
                label="Database Name"
                required
                {...register('endpoint.database', { required: 'Database name is required' })}
                type="text"
                placeholder="myapp"
              />
              {errors.endpoint && (errors.endpoint as any).database && (
                <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).database.message}</p>
              )}
            </div>

            {/* Database Credentials Section - DS PAM */}
            <div className="border-t border-[var(--d2d-border)] pt-4">
              <h4 className="text-sm font-medium text-[var(--d2d-text-primary)] mb-3">Database Credentials</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Input
                    label="Username"
                    required
                    {...register('endpoint.username', { required: 'Username is required' })}
                    type="text"
                    placeholder="iap_user"
                  />
                  {errors.endpoint && (errors.endpoint as any).username && (
                    <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).username.message}</p>
                  )}
                </div>
                <div>
                  <Input
                    label="Password"
                    required
                    {...register('endpoint.password', { required: 'Password is required' })}
                    type="password"
                    placeholder="••••••••"
                  />
                  {errors.endpoint && (errors.endpoint as any).password && (
                    <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).password.message}</p>
                  )}
                </div>
              </div>
              <div className="mt-3">
                <Input
                  label="Connection String (Optional)"
                  {...register('endpoint.connection_string')}
                  type="text"
                  placeholder="postgresql://user:pass@host:port/db?sslmode=require"
                />
                <p className="mt-1 text-sm text-[var(--d2d-text-muted)]">
                  If provided, connection string will override individual fields above
                </p>
              </div>
            </div>

            {/* Test Connection Button */}
            <div className="mt-4">
              <Button
                variant="primary"
                type="button"
                onClick={handleTestConnection}
                loading={testingConnection}
                leadingIcon={testingConnection ? undefined : <CheckCircle className="h-4 w-4" />}
              >
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </Button>
              {connectionTestResult && (
                <Banner
                  className="mt-2"
                  variant={connectionTestResult.success ? 'success' : 'danger'}
                  title={connectionTestResult.success ? 'Connection Successful!' : 'Connection Failed'}
                >
                  {connectionTestResult.message}
                </Banner>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                {/* Require first, so it is the default: managed cloud databases (Azure,
                    AWS RDS, Cloud SQL) refuse unencrypted connections, and with Require
                    the proxy encrypts the database leg for clients that do not. */}
                <Select label="SSL Mode" {...register('endpoint.ssl_mode')}>
                  <option value="require">Require</option>
                  <option value="verify-ca">Verify CA</option>
                  <option value="verify-full">Verify Full</option>
                  <option value="disable">Disable</option>
                </Select>
              </div>
              <div>
                <Input
                  label="Connection Pool Size"
                  {...register('endpoint.connection_pool_size', { valueAsNumber: true })}
                  type="number"
                  placeholder="10"
                />
              </div>
            </div>
          </div>
        );

      case 'http':
        return (
          <div className="space-y-4">
            <div>
              <Input
                label="URL"
                required
                {...register('endpoint.url', {
                  required: 'URL is required',
                  pattern: {
                    value: /^https?:\/\/.+/,
                    message: 'URL must start with http:// or https://'
                  }
                })}
                type="url"
                placeholder="https://api.example.com"
              />
              {errors.endpoint && (errors.endpoint as any).url && (
                <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).url.message}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Select label="Method" {...register('endpoint.method')}>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </Select>
              </div>
              <div>
                <Input
                  label="Timeout (seconds)"
                  {...register('endpoint.timeout', { valueAsNumber: true })}
                  type="number"
                  placeholder="30"
                />
              </div>
            </div>
            <div>
              <Textarea
                label="Headers (JSON format)"
                {...register('endpoint.headers')}
                rows={3}
                placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
              />
            </div>
          </div>
        );

      case 'ssh':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  label="Host"
                  required
                  {...register('endpoint.host', { required: 'Host is required' })}
                  type="text"
                  placeholder="server.example.com"
                />
                {errors.endpoint && (errors.endpoint as any).host && (
                  <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).host.message}</p>
                )}
              </div>
              <div>
                <Input
                  label="Port"
                  required
                  {...register('endpoint.port', {
                    required: 'Port is required',
                    valueAsNumber: true,
                    min: { value: 1, message: 'Port must be greater than 0' },
                    max: { value: 65535, message: 'Port must be less than 65536' }
                  })}
                  type="number"
                  placeholder="22"
                />
                {errors.endpoint && (errors.endpoint as any).port && (
                  <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).port.message}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input label="Username" {...register('endpoint.username')} type="text" placeholder="ubuntu" />
              </div>
              <div>
                <Select label="Key Type" {...register('endpoint.key_type')}>
                  <option value="rsa">RSA</option>
                  <option value="ed25519">Ed25519</option>
                  <option value="ecdsa">ECDSA</option>
                </Select>
              </div>
            </div>
          </div>
        );

      case 'k8s':
        return (
          <div className="space-y-4">
            <div>
              <Input
                label="Cluster Name"
                required
                {...register('endpoint.cluster_name', { required: 'Cluster name is required' })}
                type="text"
                placeholder="production-cluster"
              />
              {errors.endpoint && (errors.endpoint as any).cluster_name && (
                <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).cluster_name.message}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  label="API Server"
                  required
                  {...register('endpoint.api_server', { required: 'API server is required' })}
                  type="text"
                  placeholder="https://k8s-api.example.com:6443"
                />
                {errors.endpoint && (errors.endpoint as any).api_server && (
                  <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).api_server.message}</p>
                )}
              </div>
              <div>
                <Input label="Namespace" {...register('endpoint.namespace')} type="text" placeholder="default" />
              </div>
            </div>
            <div>
              <Textarea
                label="Kubeconfig (YAML)"
                required
                {...register('endpoint.kubeconfig', { required: 'Kubeconfig is required' })}
                className="[&_textarea]:font-mono [&_textarea]:text-sm"
                rows={12}
                placeholder={`apiVersion: v1
kind: Config
clusters:
- cluster:
    certificate-authority-data: LS0tLS1CRUdJTi...
    server: https://k8s-api.example.com:6443
  name: my-cluster
contexts:
- context:
    cluster: my-cluster
    user: my-user
  name: my-context
current-context: my-context
users:
- name: my-user
  user:
    client-certificate-data: LS0tLS1CRUdJTi...
    client-key-data: LS0tLS1CRUdJTi...`}
              />
              {errors.endpoint && (errors.endpoint as any).kubeconfig && (
                <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).kubeconfig.message}</p>
              )}
              <p className="mt-1 text-xs text-[var(--d2d-text-muted)]">
                Paste the full kubeconfig YAML content. This will be securely stored and used by CLI for connections.
              </p>
            </div>
          </div>
        );

      case 'desktop':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  label="Host / IP"
                  required
                  {...register('endpoint.host', { required: 'Host is required' })}
                  type="text"
                  placeholder="172.16.0.4"
                />
                {errors.endpoint && (errors.endpoint as any).host && (
                  <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).host.message}</p>
                )}
              </div>
              <div>
                <Input
                  label="Port"
                  required
                  {...register('endpoint.port', {
                    required: 'Port is required',
                    valueAsNumber: true,
                    min: { value: 1, message: 'Port must be greater than 0' },
                    max: { value: 65535, message: 'Port must be less than 65536' }
                  })}
                  type="number"
                  placeholder="3389"
                />
                {errors.endpoint && (errors.endpoint as any).port && (
                  <p className="mt-1 text-sm text-[var(--danger-text)]">{(errors.endpoint as any).port.message}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input label="Username" {...register('endpoint.username')} type="text" placeholder="Administrator" />
              </div>
              <div>
                <Input label="Domain" {...register('endpoint.domain')} type="text" placeholder="WORKGROUP" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Select label="Screen Size" {...register('endpoint.screen_size')}>
                  <option value="1920x1080">1920×1080 (Full HD)</option>
                  <option value="1280x720">1280×720 (HD)</option>
                  <option value="1366x768">1366×768</option>
                  <option value="2560x1440">2560×1440 (2K)</option>
                  <option value="fullscreen">Fullscreen</option>
                </Select>
              </div>
              <div>
                <Select label="Security" {...register('endpoint.security')}>
                  <option value="nla">NLA (Network Level Authentication)</option>
                  <option value="tls">TLS</option>
                  <option value="rdp">RDP</option>
                  <option value="any">Any</option>
                </Select>
              </div>
            </div>
            <div className="border-t border-[var(--d2d-border)] pt-4">
              <h4 className="text-sm font-medium text-[var(--d2d-text-primary)] mb-3">Session Controls</h4>
              <div className="grid grid-cols-2 gap-4">
                {/* ds Checkbox reports (checked, event); react-hook-form's handler wants the event. */}
                {(() => { const f = register('endpoint.clipboard_enabled'); return <Checkbox {...f} onChange={(_, e) => f.onChange(e)} defaultChecked label="Enable Clipboard" />; })()}
                {(() => { const f = register('endpoint.drive_sharing'); return <Checkbox {...f} onChange={(_, e) => f.onChange(e)} label="Enable Drive Sharing" />; })()}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderTypeField = () => {
    const labelForType = (t: string) => {
      switch (t) {
        case 'database':
          return 'Database';
        case 'http_proxy':
          return 'HTTP Proxy';
        case 'ssh':
          return 'SSH Server';
        case 'k8s':
          return 'Kubernetes';
        case 'desktop':
          return 'Desktop (RDP)';
        default:
          return t;
      }
    };

    if (isEditing && resource) {
      return (
        <>
          {/* Keep original type for submission */}
          <input type="hidden" {...register('type')} />
          {/* Show a read-only label for clarity */}
          <Input
            label="Type"
            required
            type="text"
            value={labelForType(resource.type)}
            disabled
            readOnly
          />
        </>
      );
    }

    return (
      <Select
        label="Type"
        required
        {...register('type', { required: 'Type is required' })}
        onChange={(e) => setSelectedType(e.target.value)}
        disabled={isEditing}
      >
        <option value="database">Database</option>
        <option value="http_proxy">HTTP Proxy</option>
        <option value="ssh">SSH Server</option>
        <option value="k8s">Kubernetes</option>
        <option value="desktop">Desktop (RDP)</option>
      </Select>
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEditing ? 'Edit Resource' : 'Add New Resource'}
      size="xl"
      dismissOnOverlay={false}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="resource-form" disabled={createMutation.isLoading || updateMutation.isLoading}>
            {createMutation.isLoading || updateMutation.isLoading
              ? 'Saving...'
              : isEditing
                ? 'Update Resource'
                : 'Create Resource'}
          </Button>
        </>
      }
    >
        <form id="resource-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                label="Resource Name"
                required
                {...register('external_id', { required: 'Resource name is required' })}
                type="text"
                placeholder="my-database"
              />
              {errors.external_id && (
                <p className="mt-1 text-sm text-[var(--danger-text)]">{errors.external_id.message}</p>
              )}
            </div>
            <div>
              {renderTypeField()}
              {errors.type && (
                <p className="mt-1 text-sm text-[var(--danger-text)]">{errors.type.message}</p>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-body font-medium text-[var(--d2d-text-primary)] mb-3">Endpoint Configuration</h4>
            {renderEndpointFields()}
          </div>

          <div>
            <Textarea
              label="Labels (Optional)"
              value={labelsText}
              onChange={(e) => {
                setLabelsText(e.target.value);
                // Try to parse to provide immediate feedback and keep form state in sync
                try {
                  const v = e.target.value?.trim() ? JSON.parse(e.target.value) : {};
                  setValue('labels', v, { shouldValidate: true });
                  setLabelsJsonError(null);
                } catch {
                  setLabelsJsonError('Invalid JSON');
                }
              }}
              rows={4}
              placeholder='{"environment": "production", "team": "backend"}'
            />
            {labelsJsonError && (
              <p className="mt-1 text-sm text-[var(--danger-text)]">{labelsJsonError}</p>
            )}
            <p className="mt-1 text-sm text-[var(--d2d-text-muted)]">JSON format key-value pairs</p>
          </div>

          <div>
            <Textarea
              label="Metadata (Optional)"
              value={metadataText}
              onChange={(e) => {
                setMetadataText(e.target.value);
                try {
                  const v = e.target.value?.trim() ? JSON.parse(e.target.value) : {};
                  setValue('metadata', v, { shouldValidate: true });
                  setMetadataJsonError(null);
                } catch {
                  setMetadataJsonError('Invalid JSON');
                }
              }}
              rows={4}
              placeholder='{"description": "Main application database", "owner": "team@company.com"}'
            />
            {metadataJsonError && (
              <p className="mt-1 text-sm text-[var(--danger-text)]">{metadataJsonError}</p>
            )}
            <p className="mt-1 text-sm text-[var(--d2d-text-muted)]">JSON format key-value pairs</p>
          </div>

        </form>
    </Modal>
  );
}
